import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gamesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, Code, Download, Play, Share2, DollarSign, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const GameDetail = () => {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCode, setShowCode] = useState(false);
  const [playMode, setPlayMode] = useState(false);
  const [adsEnabled, setAdsEnabled] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadGame();
  }, [gameId]);

  const loadGame = async () => {
    try {
      // Try authenticated owner view first; if that fails (e.g., public access), fall back to public endpoint
      try {
        const data = await gamesAPI.getGame(gameId);
        setGame(data);
        setAdsEnabled(data.ads_enabled || false);
        return;
      } catch (authErr) {
        const data = await gamesAPI.getPublicGame(gameId);
        setGame(data);
        setAdsEnabled(data.ads_enabled || false);
      }
    } catch (error) {
      console.error('Error loading game:', error);
      toast({
        title: 'Error',
        description: 'Failed to load game',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const id = game?.game_id || gameId;
    if (!id) {
      toast({
        title: 'Error',
        description: 'Unable to share this game right now',
        variant: 'destructive',
      });
      return;
    }

    // Use backend meta endpoint on the SAME public domain so social platforms get proper OG/Twitter previews
    // We intentionally base this on window.location.origin (gameos.dev in production)
    // rather than REACT_APP_BACKEND_URL, which may point at an internal preview host.
    const shareUrl = `${window.location.origin}/api/meta/games/${id}`;

    if (navigator.share) {
      navigator
        .share({
          title: game?.title || 'GameOS Game',
          text: 'Check out this game I built with GameOS!',
          url: shareUrl,
        })
        .catch(() => {
          // fall back to clipboard if user cancels or share fails
          navigator.clipboard.writeText(shareUrl);
          toast({
            title: 'Link Copied!',
            description: 'Game link copied to clipboard',
          });
        });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Link Copied!',
        description: 'Game link copied to clipboard',
      });
    }
  };

  const handlePlayGame = () => {
    setPlayMode(true);
  };

  const handleToggleAds = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/games/${gameId}/toggle-ads`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setAdsEnabled(!adsEnabled);
      toast({
        title: adsEnabled ? 'Ads Disabled' : 'Ads Enabled',
        description: adsEnabled 
          ? 'Monetization has been turned off for this game' 
          : 'Monetization is now active! You\'ll earn 90% of ad revenue.',
      });
    } catch (error) {
      console.error('Error toggling ads:', error);
      toast({
        title: 'Error',
        description: 'Failed to toggle ads',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading game...</div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Game not found</div>
      </div>
    );
  }

  // Full-screen play mode
  if (playMode && game) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        <div className="absolute top-4 right-4 z-10">
          <Button
            onClick={() => setPlayMode(false)}
            className="bg-red-600 hover:bg-red-700 text-white shadow-lg"
          >
            <X size={20} className="mr-2" />
            Exit Game
          </Button>
        </div>
        <iframe
          srcDoc={(() => {
            const code = game.code || '';
            const lower = code.toLowerCase();

            // If this is already a full HTML document, use it directly.
            if (lower.includes('<html')) {
              return code;
            }

            // Otherwise, treat it as JavaScript and wrap in an HTML shell.
            return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${game.title || 'Game'}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 100%; height: 100%; overflow: hidden; background: #0b1220; }
  </style>
</head>
<body>
  <script>
    try {
      ${game.code || ''}
    } catch (e) {
      document.body.innerHTML = '<div style="color: white; padding: 20px; font-family: Arial;">Error loading game: ' + e.message + '</div>';
      console.error('Game error:', e);
    }
  </script>
</body>
</html>`;
          })()}
          className="w-full h-full border-0"
          title={game.title || 'Game'}
          sandbox="allow-scripts"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Dashboard
              </Button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 focus:outline-none"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">GO</span>
                </div>
                <span className="text-xl font-bold text-white">GameOS</span>
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleShare}
                variant="outline"
                className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
              >
                <Share2 size={16} className="mr-2" />
                Share
              </Button>
              <Button
                onClick={handlePlayGame}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                <Play size={16} className="mr-2" />
                Play Game
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {/* Game Header */}
          <div>
            <h1 className="text-4xl font-bold text-white mb-4">{game.title}</h1>
            <p className="text-gray-400 text-lg mb-4">{game.description}</p>
            <div className="flex flex-wrap gap-2">
              {game.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="text-sm px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Monetization Section */}
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 rounded-2xl p-8 border border-green-500/30">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                  <DollarSign className="text-green-400" size={28} />
                  Monetization
                </h2>
                <p className="text-gray-400">
                  {adsEnabled 
                    ? '🟢 Ads are enabled - You earn 90% of revenue!' 
                    : 'Turn on ads to start earning revenue from your game'}
                </p>
              </div>
              <Button
                onClick={handleToggleAds}
                className={adsEnabled 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-green-600 hover:bg-green-700 text-white'}
              >
                {adsEnabled ? 'Disable Ads' : 'Enable Ads'}
              </Button>
            </div>
            {adsEnabled && (
              <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-400 text-sm">
                  ✅ Your game is now monetized! You keep 90% of all ad revenue generated.
                </p>
              </div>
            )}
          </div>

          {/* Game Preview */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-4">Game Preview</h2>
            {game.thumbnail && (
              <div className="bg-black/50 rounded-lg p-4 mb-4">
                <img
                  src={game.thumbnail}
                  alt={game.title}
                  className="max-w-md mx-auto"
                />
              </div>
            )}
            <div className="text-center space-y-4">
              <p className="text-gray-400">
                Game is ready! Code and assets generated successfully.
              </p>
              <Button
                onClick={handlePlayGame}
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                <Play size={20} className="mr-2" />
                Play Full Screen
              </Button>
            </div>
          </div>

          {/* Game Code */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Game Code</h2>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowCode(!showCode)}
                  variant="outline"
                  className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                >
                  <Code size={16} className="mr-2" />
                  {showCode ? 'Hide Code' : 'Show Code'}
                </Button>
                <Button
                  onClick={() => {
                    const blob = new Blob([game.code], { type: 'text/javascript' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${game.title.replace(/\s+/g, '_')}.js`;
                    a.click();
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Download size={16} className="mr-2" />
                  Download Code
                </Button>
              </div>
            </div>
            {showCode && game.code && (
              <pre className="bg-black/50 p-4 rounded-lg overflow-x-auto text-sm text-gray-300">
                <code>{game.code}</code>
              </pre>
            )}
          </div>

          {/* Game Stats */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <div className="text-gray-400 mb-2">Total Plays</div>
              <div className="text-3xl font-bold text-white">
                {game.stats?.plays || 0}
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <div className="text-gray-400 mb-2">Revenue Generated</div>
              <div className="text-3xl font-bold text-green-400">
                ${game.stats?.revenue?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-white/10">
              <div className="text-gray-400 mb-2">Created</div>
              <div className="text-lg font-semibold text-white">
                {new Date(game.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameDetail;